import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, DollarSign, ShoppingCart, Receipt, RefreshCcw, Download } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from 'recharts';

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

const STATUS_COLORS = {
    new: 'bg-blue-100 text-blue-700',
    preparing: 'bg-amber-100 text-amber-700',
    ready: 'bg-emerald-100 text-emerald-700',
    delivered: 'bg-neutral-100 text-neutral-600',
    cancelled: 'bg-red-100 text-red-600',
};
const STATUS_LABELS = { new: 'Nuevo', preparing: 'Preparando', ready: 'Listo', delivered: 'Entregado', cancelled: 'Cancelado' };

function StatCard({ icon: Icon, label, value, color = 'text-orange-600' }) {
    return (
        <div className="rounded-2xl border bg-card p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Icon className={`h-3.5 w-3.5 ${color}`} />{label}
            </div>
            <div className="text-2xl font-bold tabular-nums">{value}</div>
        </div>
    );
}

export default function AliadoSalesPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (dateFrom) params.set('date_from', dateFrom);
            if (dateTo) params.set('date_to', dateTo);
            const { data: res } = await api.get(`/me/oknegocio/sales?${params}`);
            setData(res);
        } catch { toast.error('Error al cargar ventas'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const exportCSV = () => {
        if (!data?.orders?.length) return toast.info('Sin datos para exportar');
        const rows = [
            ['Fecha', 'Cliente', 'Items', 'Total', 'Pago', 'Estado'],
            ...data.orders.map(o => [
                o.created_at?.slice(0, 10),
                o.customer_name,
                o.items?.length || 0,
                o.total,
                o.payment_method,
                STATUS_LABELS[o.status] || o.status,
            ])
        ];
        const csv = rows.map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'ventas.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-xl font-bold flex items-center gap-2"><TrendingUp className="h-5 w-5 text-orange-600" /> Ventas</h1>
                    <p className="text-sm text-muted-foreground">Resumen de ingresos y actividad.</p>
                </div>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={exportCSV} className="h-8 text-xs">
                        <Download className="h-3.5 w-3.5 mr-1" /> Exportar CSV
                    </Button>
                    <Button size="sm" variant="outline" onClick={load} className="h-8"><RefreshCcw className="h-3.5 w-3.5" /></Button>
                </div>
            </div>

            {/* Date filter */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                    <label className="text-xs text-muted-foreground">Desde</label>
                    <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-7 text-xs w-36" />
                </div>
                <div className="flex items-center gap-1">
                    <label className="text-xs text-muted-foreground">Hasta</label>
                    <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-7 text-xs w-36" />
                </div>
                <Button size="sm" onClick={load} className="h-7 text-xs bg-orange-600 hover:bg-orange-700">Filtrar</Button>
                {(dateFrom || dateTo) && (
                    <Button size="sm" variant="ghost" onClick={() => { setDateFrom(''); setDateTo(''); }} className="h-7 text-xs">Limpiar</Button>
                )}
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Cargando ventas…</div>
            ) : !data ? null : (
                <>
                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <StatCard icon={DollarSign} label="Ingresos totales" value={fmt(data.total_revenue)} color="text-emerald-600" />
                        <StatCard icon={ShoppingCart} label="Órdenes" value={data.total_orders} />
                        <StatCard icon={Receipt} label="Ticket promedio" value={fmt(data.avg_ticket)} color="text-violet-600" />
                    </div>

                    {/* Revenue chart */}
                    {data.chart?.length > 0 && (
                        <div className="rounded-2xl border bg-card p-4">
                            <h2 className="text-sm font-semibold mb-4">Ingresos por día</h2>
                            <ResponsiveContainer width="100%" height={180}>
                                <BarChart data={data.chart} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                                    <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                                    <Tooltip formatter={(v) => fmt(v)} labelStyle={{ fontSize: 10 }} />
                                    <Bar dataKey="revenue" fill="#ea580c" radius={[4, 4, 0, 0]} name="Ingresos" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Orders table */}
                    <div className="rounded-2xl border bg-card">
                        <div className="px-4 py-3 border-b">
                            <h2 className="text-sm font-semibold">Detalle de órdenes</h2>
                        </div>
                        {data.orders?.length === 0 ? (
                            <div className="p-8 text-center text-sm text-muted-foreground">Sin órdenes en el período seleccionado.</div>
                        ) : (
                            <div className="divide-y">
                                {data.orders.map(o => (
                                    <div key={o.id} className="flex items-center justify-between px-4 py-2.5 gap-3">
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{o.customer_name}</div>
                                            <div className="text-xs text-muted-foreground">{o.created_at?.slice(0, 10)} · {o.payment_method}</div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className="text-sm font-semibold">{fmt(o.total)}</span>
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_COLORS[o.status] || 'bg-neutral-100 text-neutral-600'}`}>
                                                {STATUS_LABELS[o.status] || o.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
